import { useState } from 'owa-react';
import { observer } from 'owa-mobx-react';
import { shouldUseAppHostSearch } from 'owa-app-host-search/lib/utils/shouldUseAppHostSearch';
import React from 'react';
import type { SearchBoxContainerHandle } from 'owa-search/lib/types/SearchBoxContainerHandle';
import { SearchBox, type SearchBoxHandle } from 'owa-search/lib/components/SearchBox';
import { SearchScenarioId } from 'owa-search-store';
import { isReadingPanePositionRight, getSearchBoxWidthFromListView } from 'owa-mail-layout';
import { getStore as getMailSearchStore } from '../store/store';
import { shouldShowInScopeSelector } from 'owa-mail-search-utils';
import { AdvancedSearchWrapper } from 'owa-mail-advanced-search';
import {
    isSearchBarDisabled,
    getSearchPlaceHolderText,
    getAriaLabelForSearchBox,
} from '../utils/mailSearchUtils';
import { isLocalDataBackedMailboxInfo } from 'owa-account-source-list/lib/utils/isLocalDataBackedAccount';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import getSearchProvider from '../selectors/getSearchProvider';
import getSelectedSearchScopeDisplayName from '../utils/getSelectedSearchScopeDisplayName';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import { getPreviousNode, getSelectedNode } from 'owa-mail-folder-forest-store';
import isWin32OverlayEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';

const WIN32_SEARCH_BOX_WIDTH = 300;

const defaultExport: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        Omit<React.RefAttributes<SearchBoxContainerHandle>, 'ref'> &
            React.RefAttributes<SearchBoxContainerHandle>
    >
> = observer(
    React.forwardRef(function MailSearchBoxContainer(
        _props: {},
        ref: React.Ref<SearchBoxContainerHandle>
    ) {
        const searchBox = React.useRef<SearchBoxHandle | null>(null);
        const [isHovered, setIsHovered] = useState<boolean>(false);
        const {
            shouldShowAdvancedSearch,
            advancedSearchViewState,
            initialSearchScope,
            staticSearchScope,
            staticSearchScopeList,
            selectedMailboxInfo,
        } = getMailSearchStore();
        const previousNode = getPreviousNode();
        const isSuggestionsCalloutDisabled = !shouldShowInScopeSelector(initialSearchScope);
        const isSearchDisabled = isSearchBarDisabled.get();
        /*
         * Determine whether advanced search is supported in the current mailbox
         * Must use the moduleContextMailboxInfo, since it's up to date when hovering over the searchbox,
         * whereas selectedMailboxInfo is only updated after starting the search session
         */
        const isAdvancedSearchSupported =
            !shouldUseAppHostSearch() &&
            !isLocalDataBackedMailboxInfo(getModuleContextMailboxInfo());

        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * Publicly  exposed function that allows parent to set focus inside of the
                 * search input when search scope is empty on click of search scope item
                 */
                onSearchScopeSelected(searchScenarioId: SearchScenarioId) {
                    searchBox.current?.onSearchScopeSelected(searchScenarioId);
                },
                /**
                 * Publicly  exposed function that allows parent to set focus inside of the
                 * search input.
                 */
                setFocus() {
                    searchBox.current?.focusSearchInput();
                },
            }),
            []
        );
        /**
         * This function gets the max width of the search box.
         */
        const getCustomCollapsedMaxWidth = (): number | undefined => {
            if (isWin32OverlayEnabled()) {
                return WIN32_SEARCH_BOX_WIDTH;
            }

            /**
             * If the user is in 3 column view, the max width of the search box should
             * match the width of the message list. Otherwise, it's unbounded.
             */
            return isReadingPanePositionRight() ? getSearchBoxWidthFromListView() : undefined;
        };

        const onMouseLeaveCallback = React.useCallback(() => {
            setIsHovered(false);
        }, [isHovered]);

        const onMouseEnterCallback = React.useCallback(() => {
            setIsHovered(true);
        }, [isHovered]);

        const onHoverHandlers = React.useMemo(
            () => ({
                onMouseEnter: onMouseEnterCallback,
                onMouseLeave: onMouseLeaveCallback,
            }),
            []
        );

        const setSearchBoxRef = React.useCallback((searchBoxRef: SearchBoxHandle | null) => {
            searchBox.current = searchBoxRef;
        }, []);

        const renderAdvancedSearch = React.useCallback(() => {
            return isSuggestionsCalloutDisabled ||
                !isAdvancedSearchSupported ||
                isSearchDisabled ? (
                /* eslint-disable-next-line react/jsx-no-useless-fragment -- (https://aka.ms/OWALintWiki)
                 * Don't use react fragments if there are no children
                 *	> Fragments should contain more than one child - otherwise, there’s no need for a Fragment at all. */
                <></>
            ) : (
                <AdvancedSearchWrapper
                    shouldShowAdvancedSearch={shouldShowAdvancedSearch}
                    scenarioId={SearchScenarioId.Mail}
                    advancedSearchViewState={advancedSearchViewState}
                    initialSearchScope={initialSearchScope}
                    staticSearchScope={staticSearchScope}
                    staticSearchScopeList={staticSearchScopeList}
                    mailboxInfo={selectedMailboxInfo ?? getModuleContextMailboxInfo()}
                    previousFolderId={getSelectedNode().id || previousNode?.id}
                />
            );
        }, [
            isSuggestionsCalloutDisabled,
            isAdvancedSearchSupported,
            isSearchDisabled,
            shouldShowAdvancedSearch,
            advancedSearchViewState,
            initialSearchScope,
            staticSearchScope,
            staticSearchScopeList,
            selectedMailboxInfo,
            previousNode,
        ]);

        const searchProvider = getSearchProvider(staticSearchScope);

        /**
         * We always use 3S for suggestions if we're not
         * in explicit logon.
         */
        return (
            <SearchBox
                customCollapsedMaxWidth={getCustomCollapsedMaxWidth()}
                ref={setSearchBoxRef}
                renderRefiners={renderAdvancedSearch}
                searchPlaceHolderText={getSearchPlaceHolderText()}
                isDisabled={isSearchDisabled}
                scenarioId={SearchScenarioId.Mail}
                isSuggestCalloutDisabled={isSuggestionsCalloutDisabled}
                onHoverHandlers={onHoverHandlers}
                isHovered={isHovered}
                ariaLabel={getAriaLabelForSearchBox.get()}
                shouldSuppressScopedSuggestions={
                    searchProvider === SearchProvider.Offline ||
                    searchProvider === SearchProvider.UnifiedInbox
                }
                hasScopePicker={
                    !!getSelectedSearchScopeDisplayName() &&
                    shouldShowInScopeSelector(staticSearchScope)
                }
            />
        );
    }),
    'MailSearchBoxContainer'
);
export default defaultExport;
